import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { useLanguage } from "../context/LanguageContext";
import { ThemeToggle } from "../components/ThemeToggle";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api from "../api/axios";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { MessageSquare, Mail, Globe, ArrowLeft } from "lucide-react";

export const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailErrorType, setEmailErrorType] = useState(null); // "invalidFormat" veya null
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();

  const languageNames = {
    tr: "Türkçe",
    en: "English",
    de: "Deutsch",
    es: "Español",
    fr: "Français",
  };

  // Email hata mesajını errorType'a göre çevir
  const getEmailErrorMessage = () => {
    if (emailErrorType === "invalidFormat") {
      return (
        t.invalidEmailFormat ||
        (language === "tr"
          ? "Lütfen geçerli bir e-posta adresi girin"
          : "Please enter a valid email address")
      );
    }
    return "";
  };

  // Email validasyon fonksiyonu
  const validateEmail = (emailValue) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (emailValue && !emailRegex.test(emailValue)) {
      setEmailErrorType("invalidFormat");
    } else {
      setEmailErrorType(null);
    }
  };

  // Email değiştiğinde validasyonu kontrol et
  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    validateEmail(newEmail);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Email validasyonunu kontrol et
    validateEmail(email);
    if (emailErrorType === "invalidFormat") {
      const errorMsg = getEmailErrorMessage();
      toast.error(errorMsg);
      return;
    }

    // Email format kontrolü
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailErrorType("invalidFormat");
      const errorMsg = getEmailErrorMessage();
      toast.error(errorMsg);
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/forgot-password', { email, password: "placeholder" });

      setEmailSent(true);
      toast.success(t.passwordResetLinkSent);
      setLoading(false);
    } catch (error) {
      console.error("Forgot password error:", error);
      toast.error(t.errorOccurred);
      setLoading(false);
    }
  };

  const ForgotPasswordContent = (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950 flex items-center justify-center p-4 relative">
      {/* Back Button - Sol üst köşe */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-4 left-4 p-2 hover:bg-white/20 dark:hover:bg-gray-800/50 rounded-lg transition-colors text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        aria-label="Go back to home"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>

      {/* Language Selector & Theme Toggle - Sağ üst köşe */}
      <div className="absolute top-3 md:top-4 right-3 md:right-4 flex items-center space-x-2 md:space-x-3">
        <ThemeToggle />
        <Select value={language} onValueChange={setLanguage}>
          <SelectTrigger className="w-[80px] md:w-[140px] text-xs md:text-sm">
            <SelectValue>
              <div className="flex items-center space-x-1 md:space-x-2">
                <Globe className="w-3 md:w-4 h-3 md:h-4" />
                <span className="hidden md:inline">{languageNames[language] || languageNames.tr}</span>
              </div>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tr" className="text-xs md:text-sm">{languageNames.tr}</SelectItem>
            <SelectItem value="en" className="text-xs md:text-sm">{languageNames.en}</SelectItem>
            <SelectItem value="de" className="text-xs md:text-sm">{languageNames.de}</SelectItem>
            <SelectItem value="es" className="text-xs md:text-sm">{languageNames.es}</SelectItem>
            <SelectItem value="fr" className="text-xs md:text-sm">{languageNames.fr}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img 
            src="/logo-256.webp" 
            alt="FeedPanels Logo" 
            className="w-40 h-40 object-contain mb-2 mx-auto"
          />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t.forgotPasswordTitle || "Şifremi Unuttum"}
          </h1>
        </div>

        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="dark:text-white">{t.forgotPasswordTitle || "Forgot Password"}</CardTitle>
            <CardDescription className="dark:text-gray-400">
              {emailSent
                ? t.checkYourEmail || "Check your email"
                : t.forgotPasswordDescription ||
                  "Enter your email address and we'll send you a password reset link"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!emailSent ? (
              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div className="space-y-2">
                  <Label htmlFor="email" className="dark:text-gray-300">{t.emailLabel || "Email"}</Label>
                  <Input
                    id="email"
                    type="text"
                    inputMode="email"
                    placeholder={t.emailPlaceholder || "you@company.com"}
                    value={email}
                    onChange={handleEmailChange}
                    onBlur={() => validateEmail(email)}
                    required
                    data-testid="forgot-password-email-input"
                    className={emailErrorType ? "border-destructive dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400" : "dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"}
                  />
                  {emailErrorType && (
                    <p className="text-sm text-destructive dark:text-red-400 mt-1">
                      {getEmailErrorMessage()}
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  data-testid="forgot-password-submit-button"
                >
                  {loading
                    ? t.sending || "Sending..."
                    : t.sendPasswordResetLink || "Send Password Reset Link"}
                </Button>
              </form>
            ) : (
              <div className="space-y-4 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-gray-700/50 text-green-600 dark:text-green-400 rounded-full mb-4">
                  <Mail size={32} />
                </div>
                <p className="text-gray-600 dark:text-gray-300">
                  {t.passwordResetLinkSentTo ||
                    "Password reset link has been sent to"}{" "}
                  <strong>{email}</strong>.
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t.checkEmailInbox ||
                    "Please check your email inbox. If you don't see the email, check your spam folder."}
                </p>
                <Button
                  onClick={() => {
                    setEmailSent(false);
                    setEmail("");
                  }}
                  variant="outline"
                  className="w-full"
                >
                  {t.sendNewEmail || "Send New Email"}
                </Button>
              </div>
            )}
          </CardContent>
          <CardFooter className="flex flex-col space-y-2 dark:border-t dark:border-gray-700">
            <Link
              to="/login"
              className="text-sm text-cyan-600 dark:text-cyan-400 hover:underline font-medium text-center"
              data-testid="back-to-login-link"
            >
              {t.backToSignIn || "Back to Sign In"}
            </Link>
            <div className="text-sm text-center text-gray-600 dark:text-gray-400">
              {t.dontHaveAccount || "Don't have an account?"}{" "}
              <Link
                to="/register"
                className="text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
                data-testid="register-link"
              >
                {t.signUp || "Sign up"}
              </Link>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Forgot Password - FeedPanels</title>
        <meta name="description" content="Reset your FeedPanels password. Enter your email to receive a secure link and regain access to your account." />
        <link rel="canonical" href="https://feedpanels.com/forgot-password" />
      </Helmet>
      {ForgotPasswordContent}
    </>
  );
};
